import { forwardRef } from 'react';
import { cn } from '../utils/cn';
import { Icon, type IconName } from './Icon';

export type BadgeTone =
  'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'attention' | 'teal' | 'navy';

// Borders match each fill so the badge reads as a single tinted block; only the
// neutral tone needs a visible edge to separate it from a white surface.
const toneClasses: Record<BadgeTone, string> = {
  neutral: 'bg-surface-sunken text-content-secondary border-line',
  info: 'bg-info-subtle text-info border-info-subtle',
  success: 'bg-success-subtle text-success border-success-subtle',
  warning: 'bg-warning-subtle text-warning border-warning-subtle',
  danger: 'bg-danger-subtle text-danger border-danger-subtle',
  attention: 'bg-attention-subtle text-attention border-attention-subtle',
  teal: 'bg-teal-subtle text-teal border-teal-subtle',
  navy: 'bg-navy text-navy-contrast border-navy',
};

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  icon?: IconName;
  size?: 'sm' | 'md';
  /** Small square swatch used in legends, where the icon would be noise. */
  swatch?: string;
}

/**
 * Badges are rectangular with an 8px radius, not pills — enterprise density,
 * not consumer chips. Callers pass an icon whenever the badge carries status,
 * so meaning never rests on the tone colour alone.
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { tone = 'neutral', icon, size = 'sm', swatch, className, children, ...rest },
  ref,
) {
  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-md border font-medium',
        size === 'sm' ? 'px-1.5 py-0.5 text-micro' : 'px-2 py-1 text-dense',
        toneClasses[tone],
        className,
      )}
      {...rest}
    >
      {swatch ? (
        <span
          aria-hidden="true"
          className="h-2 w-2 shrink-0 rounded-sm"
          style={{ backgroundColor: swatch }}
        />
      ) : null}
      {icon ? <Icon name={icon} size={size === 'sm' ? 11 : 13} strokeWidth={2} /> : null}
      <span className="truncate">{children}</span>
    </span>
  );
});
